import { FC, PropsWithChildren, useState } from "react";
import { DateTime } from "luxon";
import { Button, InputText, Span } from "@goodfill/ui-components";
import { PersonalInformation } from "@goodfill/domain-customer-application-types";

interface Props {
  saveAccountInformation?: ({ lorem }: { lorem: string }) => void;
  saveFinancialInformation?: ({ lorem }: { lorem: string }) => void;
  savePersonalInformation?: ({ name, lorem }: { name: string; lorem: string }) => void;
  personalInformation: PersonalInformation;
}

type FieldProps = PropsWithChildren<{
  label: string;
}>;

const Field: FC<FieldProps> = ({ children, label }) => {
  return (
    <div className="flex flex-col md:flex-row justify-between items-center gap-2 md:gap-4 border-b border-neutral-110">
      <div className="field-label">
        <Span font="light" size="sm">
          {label}
        </Span>
      </div>
      {children}
    </div>
  );
};

export const PersonalInformationSection: FC<Props> = ({
  saveAccountInformation,
  saveFinancialInformation,
  savePersonalInformation,
  personalInformation,
}) => {
  const hiddenDigitsBeforeSocialSecurityNumber = "*".repeat(personalInformation.socialSecurityNumber.length - 4);
  const fullSSN = hiddenDigitsBeforeSocialSecurityNumber
    .replace(/(.{3})/g, "$1-")
    .concat("-")
    .concat(personalInformation.socialSecurityNumber.slice(-4));
  const editLabel = "edit";
  const saveLabel = "save";

  const [name] = useState("Jane Smith");
  const [lorem] = useState("lorem");

  const [accountInformationIsEditing, setAccountInformationIsEditing] = useState(false);
  const [financialInformationIsEditing, setFinancialInformationIsEditing] = useState(false);
  const [personalInformationIsEditing, setPersonalInformationIsEditing] = useState(false);

  const onClickAccountInformationAction = () => {
    if (accountInformationIsEditing) {
      saveAccountInformation?.({ lorem });
      setAccountInformationIsEditing(false);
    } else {
      setAccountInformationIsEditing(true);
    }
  };

  const onClickFinancialInformationAction = () => {
    if (financialInformationIsEditing) {
      saveFinancialInformation?.({ lorem });
      setFinancialInformationIsEditing(false);
    } else {
      setFinancialInformationIsEditing(true);
    }
  };

  const onClickPersonalInformationAction = () => {
    if (personalInformationIsEditing) {
      savePersonalInformation?.({ lorem, name });
      setPersonalInformationIsEditing(false);
    } else {
      setPersonalInformationIsEditing(true);
    }
  };

  return (
    <div className="page-account-info-personal-info">
      <div className="flex flex-col my-1 md:my-2 mx-4 md:mx-8 px-4 md:px-8 gap-4 md:gap-8">
        <div className="flex flex-col">
          <div className="flex items-center justify-between my-2 md:my-4">
            <Span font="semibold" size="lg">
              General
            </Span>
            <Button fill className="w-fit" size="small" onClick={onClickPersonalInformationAction}>
              {personalInformationIsEditing ? saveLabel : editLabel}
            </Button>
          </div>
          <div className="flex flex-col px-1 md:px-2">
            <Field label="First Name">
              <InputText
                neutral={true}
                value={personalInformation.name.first}
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="Last Name">
              <InputText
                neutral={true}
                value={personalInformation.name.last}
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="Date of birth">
              <InputText
                neutral={true}
                value={DateTime.fromISO(personalInformation.dateOfBirth).toLocaleString(DateTime.DATE_SHORT)}
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="Country of birth">
              <InputText
                neutral={true}
                value={personalInformation.countryOfBirth.countryIsoCode}
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="Marital status">
              <InputText
                neutral={true}
                value={`${personalInformation.maritalStatus
                  .charAt(0)
                  .toUpperCase()}${personalInformation.maritalStatus.slice(1)}`}
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="Number of dependents">
              <InputText
                neutral={true}
                placeholder="salutation"
                value={personalInformation.numberOfDependents}
                disabled={!personalInformationIsEditing}
              />
            </Field>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex items-center justify-between my-2 md:my-4">
            <Span font="semibold" size="lg">
              Residence
            </Span>
            <Button fill className="w-fit" size="small" onClick={onClickFinancialInformationAction}>
              {financialInformationIsEditing ? saveLabel : editLabel}
            </Button>
          </div>
          <div className="flex flex-col px-1 md:px-2">
            <Field label="Street 1">
              <InputText
                neutral={true}
                value={personalInformation.residence.street1}
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="Street 2">
              <InputText
                neutral={true}
                value={personalInformation.residence.street2 || ""}
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="City">
              <InputText
                neutral={true}
                value={personalInformation.residence.city}
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="State">
              <InputText
                neutral={true}
                value={personalInformation.residence.state.replace(/^US-/, "")}
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="Country">
              <InputText
                neutral={true}
                value={personalInformation.residence.country.countryIsoCode}
                disabled={!personalInformationIsEditing}
              />
            </Field>

            <Field label="Zip code">
              <InputText
                neutral={true}
                value={personalInformation.residence.zipCode}
                disabled={!personalInformationIsEditing}
              />
            </Field>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex items-center justify-between my-2 md:my-4">
            <Span font="semibold" size="lg">
              Contact
            </Span>
            <Button fill className="w-fit" size="small" onClick={onClickAccountInformationAction}>
              {accountInformationIsEditing ? saveLabel : editLabel}
            </Button>
          </div>
          <div className="flex flex-col px-1 md:px-2">
            <Field label="Phone">
              <InputText
                neutral={true}
                value={
                  personalInformation.phones.length > 0
                    ? `${personalInformation.phones[0].countryCallingCode} ${personalInformation.phones[0].number}`
                    : ""
                }
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="Email">
              <InputText neutral={true} value={personalInformation.email} disabled={!personalInformationIsEditing} />
            </Field>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex items-center justify-between my-2 md:my-4">
            <Span font="semibold" size="lg">
              US Data
            </Span>
            <Button fill className="w-fit" size="small" onClick={onClickAccountInformationAction}>
              {accountInformationIsEditing ? saveLabel : editLabel}
            </Button>
          </div>
          <div className="flex flex-col px-1 md:px-2">
            <Field label="US Legal Residence">
              <InputText
                neutral={true}
                value={personalInformation.hasUSLegalResidence ? "Yes" : "No"}
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="US Tax Residence">
              <InputText
                neutral={true}
                value={personalInformation.hasUSTaxResidence ? "Yes" : "No"}
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="Social Security Number">
              <InputText neutral={true} value={fullSSN} disabled={!personalInformationIsEditing} />
            </Field>
            <Field label="Citizenship">
              <InputText
                neutral={true}
                value={
                  personalInformation.citizenships.length > 0 && personalInformation.citizenships[0].countryIsoCode
                    ? personalInformation.citizenships[0].countryIsoCode
                    : ""
                }
                disabled={!personalInformationIsEditing}
              />
            </Field>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex items-center justify-between my-2 md:my-4">
            <Span font="semibold" size="lg">
              Employment
            </Span>
            <Button fill className="w-fit" size="small" onClick={onClickAccountInformationAction}>
              {accountInformationIsEditing ? saveLabel : editLabel}
            </Button>
          </div>
          <div className="flex flex-col px-1 md:px-2">
            <Field label="Status">
              <InputText
                neutral={true}
                value={
                  personalInformation.employment.status
                    ? `${personalInformation.employment.status
                        .charAt(0)
                        .toUpperCase()}${personalInformation.employment.status.slice(1)}`
                    : ""
                }
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="Employer">
              <InputText
                neutral={true}
                value={personalInformation.employment.details?.employer}
                disabled={!personalInformationIsEditing}
              />
            </Field>
            <Field label="Business">
              <InputText
                neutral={true}
                value={personalInformation.employment.details?.employerBusiness}
                disabled={!personalInformationIsEditing}
              />
            </Field>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex items-center justify-between my-2 md:my-2">
            <Span font="semibold" size="lg">
              Employer Address
            </Span>
            <Button fill className="w-fit" size="small" onClick={onClickFinancialInformationAction}>
              {financialInformationIsEditing ? saveLabel : editLabel}
            </Button>
          </div>
          <div className="flex flex-col px-1 md:px-2">
            <Field label="Country">
              <InputText
                neutral={true}
                value={personalInformation.employment.details?.employerCountry.countryIsoCode}
                disabled={!personalInformationIsEditing}
              />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
};
